import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Plus, Trash2, Edit2, FileText, Users, Gift, Calendar, Download, Mail } from "lucide-react";
import { API } from "../config";
import { useAuth } from "../context/AuthContext";
import { Toast } from "../components/Toast";
import AdminCreateOrder from "../components/AdminCreateOrder";
import CreditAccounts from "../components/CreditAccounts";

export default function AdminPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [scrapeUrl, setScrapeUrl] = useState("");
  const [scrapeCategory, setScrapeCategory] = useState("Ink Cartridges");
  const [scraping, setScraping] = useState(false);
  const [toast, setToast] = useState(null);
  const [editProduct, setEditProduct] = useState(null);
  const [showProductForm, setShowProductForm] = useState(false);
  const [giftCardFilter, setGiftCardFilter] = useState("all");
  const [productForm, setProductForm] = useState({
    name: "", sku: "", description: "", price: "", original_price: "",
    category: "", brand: "", image_url: "", stock: "100"
  });
  const [savingProduct, setSavingProduct] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, ordersRes, productsRes, usersRes] = await Promise.all([
          axios.get(`${API}/admin/stats`),
          axios.get(`${API}/orders`),
          axios.get(`${API}/products`),
          axios.get(`${API}/admin/users`)
        ]);
        setStats(statsRes.data);
        setOrders(ordersRes.data);
        setProducts(productsRes.data);
        setCustomers(usersRes.data);
      } catch (err) {
        console.error("Failed to fetch admin data:", err);
      } finally {
        setLoading(false);
      }
    };
    if (user?.role === "admin") fetchData();
  }, [user]);

  const handleScrape = async (e) => {
    e.preventDefault();
    setScraping(true);
    try {
      const res = await axios.post(`${API}/admin/scrape`, {
        url: scrapeUrl,
        category: scrapeCategory
      });
      setToast({ message: res.data.message, type: "success" });
      const productsRes = await axios.get(`${API}/products`);
      setProducts(productsRes.data);
    } catch (err) {
      setToast({ message: "Failed to scrape products", type: "error" });
    } finally {
      setScraping(false);
    }
  };

  const updateOrderStatus = async (orderId, status) => {
    try {
      await axios.put(`${API}/orders/${orderId}/status?status=${status}`);
      setOrders(orders.map(o => o.id === orderId ? { ...o, status } : o));
      setToast({ message: "Order status updated", type: "success" });
    } catch (err) {
      setToast({ message: "Failed to update order", type: "error" });
    }
  };

  const deleteOrder = async (orderId) => {
    if (!window.confirm("Delete this order/invoice? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/orders/${orderId}`);
      setOrders(orders.filter(o => o.id !== orderId));
      setToast({ message: "Order deleted", type: "success" });
    } catch (err) {
      setToast({ message: "Failed to delete order", type: "error" });
    }
  };

  const backdateOrder = async (orderId, currentIso) => {
    const current = currentIso ? currentIso.slice(0, 10) : "";
    const next = window.prompt("New invoice date (YYYY-MM-DD):", current);
    if (!next || next === current) return;
    try {
      const res = await axios.patch(`${API}/admin/orders/${orderId}`, { created_at: next });
      setOrders((prev) => prev.map((o) => (o.id === orderId ? { ...o, created_at: res.data.created_at } : o)));
      setToast({ message: "Invoice date updated", type: "success" });
    } catch (err) {
      setToast({ message: err.response?.data?.detail || "Failed to update date", type: "error" });
    }
  };

  const emailOrderDocument = async (orderId) => {
    if (!window.confirm("Email the order confirmation / pro forma PDF to the customer?")) return;
    try {
      const res = await axios.post(`${API}/admin/orders/${orderId}/email`);
      setToast({ message: res.data.message, type: "success" });
      setOrders((prev) => prev.map((o) => o.id === orderId ? { ...o, last_email_sent_at: new Date().toISOString() } : o));
    } catch (err) {
      setToast({ message: err.response?.data?.detail || "Failed to send email", type: "error" });
    }
  };

  const exportOrdersCsv = async () => {
    try {
      const res = await axios.get(`${API}/admin/orders/export`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const link = document.createElement("a");
      const today = new Date().toISOString().slice(0, 10);
      link.href = url;
      link.setAttribute("download", `Carepoint-Orders-${today}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      setToast({ message: "Failed to export CSV", type: "error" });
    }
  };

  const exportSingleOrderCsv = async (orderId) => {
    try {
      const res = await axios.get(`${API}/admin/orders/${orderId}/export`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Carepoint-Order-${orderId.slice(0, 8).toUpperCase()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      setToast({ message: "Failed to export order CSV", type: "error" });
    }
  };

  const deleteCustomer = async (customerId) => {
    if (!window.confirm("Delete this customer? Their cart will also be removed.")) return;
    try {
      await axios.delete(`${API}/admin/users/${customerId}`);
      setCustomers(customers.filter(c => c.id !== customerId));
      setToast({ message: "Customer deleted", type: "success" });
    } catch (err) {
      setToast({ message: err.response?.data?.detail || "Failed to delete customer", type: "error" });
    }
  };

  const deleteProduct = async (productId) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await axios.delete(`${API}/products/${productId}`);
      setProducts(products.filter(p => p.id !== productId));
      setToast({ message: "Product deleted", type: "success" });
    } catch (err) {
      setToast({ message: "Failed to delete product", type: "error" });
    }
  };

  const openEditProduct = (product) => {
    setEditProduct(product);
    setProductForm({
      name: product.name, sku: product.sku, description: product.description || "",
      price: String(product.price), original_price: product.original_price ? String(product.original_price) : "",
      category: product.category, brand: product.brand || "", image_url: product.image_url || "",
      stock: String(product.stock || 100)
    });
    setShowProductForm(true);
  };

  const openAddProduct = () => {
    setEditProduct(null);
    setProductForm({ name: "", sku: "", description: "", price: "", original_price: "", category: "", brand: "", image_url: "", stock: "100" });
    setShowProductForm(true);
  };

  const handleSaveProduct = async (e) => {
    e.preventDefault();
    setSavingProduct(true);
    const data = {
      ...productForm,
      price: parseFloat(productForm.price),
      original_price: productForm.original_price ? parseFloat(productForm.original_price) : null,
      stock: parseInt(productForm.stock || "100"),
    };
    try {
      if (editProduct) {
        await axios.put(`${API}/products/${editProduct.id}`, data);
        setProducts(products.map(p => p.id === editProduct.id ? { ...p, ...data } : p));
        setToast({ message: "Product updated", type: "success" });
      } else {
        const res = await axios.post(`${API}/products`, data);
        setProducts([res.data, ...products]);
        setToast({ message: "Product added", type: "success" });
      }
      setShowProductForm(false);
    } catch (err) {
      setToast({ message: err.response?.data?.detail || "Failed to save product", type: "error" });
    } finally {
      setSavingProduct(false);
    }
  };

  if (!user || user.role !== "admin") {
    return (
      <div className="max-w-7xl mx-auto px-4 py-12 text-center">
        <p className="text-gray-500">Admin access required</p>
        <Link to="/login" className="btn-primary mt-4 inline-block">Sign In</Link>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center py-24">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-gray-800 mb-8" data-testid="admin-title">Admin Dashboard</h1>

      {/* Tabs */}
      <div className="flex gap-4 mb-8 border-b">
        {["dashboard", "orders", "create-order", "credit", "customers", "products", "scraper"].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`pb-3 px-4 font-medium capitalize ${activeTab === tab ? "border-b-2 border-[#00a99d] text-[#00a99d]" : "text-gray-500"}`}
            data-testid={`tab-${tab}`}
          >
            {tab.replace("-", " ")}
          </button>
        ))}
      </div>

      {/* Dashboard Tab */}
      {activeTab === "dashboard" && stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="admin-stats">
          <div className="card p-4">
            <p className="text-sm text-gray-500">Products</p>
            <p className="text-2xl font-bold">{stats.total_products}</p>
          </div>
          <div className="card p-4">
            <p className="text-sm text-gray-500">Orders</p>
            <p className="text-2xl font-bold">{stats.total_orders}</p>
          </div>
          <div className="card p-4">
            <p className="text-sm text-gray-500">Users</p>
            <p className="text-2xl font-bold">{stats.total_users}</p>
          </div>
          <div className="card p-4">
            <p className="text-sm text-gray-500">Pending</p>
            <p className="text-2xl font-bold text-yellow-600">{stats.pending_orders}</p>
          </div>
          <div className="card p-4">
            <p className="text-sm text-gray-500">Revenue</p>
            <p className="text-2xl font-bold text-green-600">&pound;{stats.total_revenue.toFixed(2)}</p>
          </div>
        </div>
      )}

      {/* Orders Tab */}
      {activeTab === "orders" && (
        <div className="card overflow-x-auto" data-testid="admin-orders">
          <div className="p-4 border-b flex flex-wrap gap-3 items-center">
            <button
              onClick={() => setGiftCardFilter(giftCardFilter === "eligible" ? "all" : "eligible")}
              className={`inline-flex items-center gap-2 text-sm font-semibold px-3 py-1.5 rounded-md border transition ${
                giftCardFilter === "eligible"
                  ? "bg-amber-400 border-amber-500 text-amber-900"
                  : "border-gray-300 text-gray-700 hover:border-amber-400"
              }`}
              data-testid="filter-gift-card-eligible"
            >
              <Gift className="w-4 h-4" />
              {giftCardFilter === "eligible" ? "Showing Gift Card eligible" : "Show Gift Card eligible"}
            </button>
            <span className="text-xs text-gray-500" data-testid="gift-card-pending-count">
              Pending dispatch:{" "}
              <strong>
                {orders.filter((o) => o.gift_card_eligible && !o.gift_card_dispatched).length}
              </strong>
            </span>
            <button
              onClick={exportOrdersCsv}
              className="ml-auto inline-flex items-center gap-2 text-sm font-semibold px-3 py-1.5 rounded-md border border-gray-300 text-gray-700 hover:border-[#00a99d] hover:text-[#00a99d] transition"
              data-testid="export-orders-csv"
              title="Download every order as a CSV (UTF-8, Excel-friendly)"
            >
              <Download className="w-4 h-4" />
              Export to CSV
            </button>
          </div>
          <table className="table">
            <thead>
              <tr>
                <th>Order ID</th>
                <th>Date</th>
                <th>Items</th>
                <th>Total</th>
                <th>Status</th>
                <th>Gift Card</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {orders
                .filter((o) => giftCardFilter === "all" || o.gift_card_eligible)
                .map((order) => (
                <tr key={order.id} className={order.gift_card_eligible && !order.gift_card_dispatched ? "bg-amber-50" : ""}>
                  <td className="font-mono text-sm">{order.id.slice(0, 8)}</td>
                  <td>
                    <span className="inline-flex items-center gap-1">
                      {new Date(order.created_at).toLocaleDateString()}
                      <button
                        type="button"
                        onClick={() => backdateOrder(order.id, order.created_at)}
                        title="Edit invoice date"
                        className="text-gray-400 hover:text-[#00a99d]"
                        data-testid={`backdate-${order.id}`}
                      >
                        <Calendar className="w-3.5 h-3.5" />
                      </button>
                    </span>
                  </td>
                  <td>{order.items.length}</td>
                  <td>&pound;{order.total.toFixed(2)}</td>
                  <td><span className={`badge status-${order.status}`}>{order.status}</span></td>
                  <td data-testid={`admin-gift-card-${order.id}`}>
                    {order.gift_card_eligible ? (
                      <div className="flex flex-col items-start gap-1">
                        {order.gift_card_choice ? (
                          <span
                            className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded text-white"
                            style={{ background: order.gift_card_choice.color || "#1f2937" }}
                            data-testid={`admin-gift-card-choice-${order.id}`}
                          >
                            <Gift className="w-3 h-3" />
                            {order.gift_card_choice.name}
                          </span>
                        ) : (
                          <span className="text-[10px] text-amber-700 font-medium" data-testid={`admin-gift-card-pending-choice-${order.id}`}>
                            Awaiting customer choice
                          </span>
                        )}
                        <button
                          onClick={async () => {
                            try {
                              await axios.put(
                                `${API}/orders/${order.id}/gift-card?dispatched=${!order.gift_card_dispatched}`
                              );
                              setOrders(orders.map((o) =>
                                o.id === order.id ? { ...o, gift_card_dispatched: !order.gift_card_dispatched } : o
                              ));
                            } catch (err) {
                              console.error("Failed to update gift card status:", err);
                            }
                          }}
                          className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-md transition ${
                            order.gift_card_dispatched
                              ? "bg-green-100 text-green-800 hover:bg-green-200"
                              : "bg-amber-400 text-amber-900 hover:bg-amber-500"
                          }`}
                          title={order.gift_card_dispatched ? "Click to mark as NOT dispatched" : "Mark gift card as dispatched"}
                          data-testid={`toggle-gift-card-${order.id}`}
                        >
                          <Gift className="w-3.5 h-3.5" />
                          {order.gift_card_dispatched ? "Dispatched" : "Pending"}
                        </button>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-300">—</span>
                    )}
                  </td>
                  <td>
                    <div className="flex items-center gap-2">
                      <select 
                        value={order.status}
                        onChange={(e) => updateOrderStatus(order.id, e.target.value)}
                        className="text-sm border rounded px-2 py-1"
                      >
                        <option value="pending">Pending</option>
                        <option value="processing">Processing</option>
                        <option value="shipped">Shipped</option>
                        <option value="delivered">Delivered</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                      <button
                        onClick={async () => {
                          try {
                            const res = await axios.get(`${API}/orders/${order.id}/invoice`, { responseType: 'blob' });
                            const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                            const link = document.createElement('a');
                            const isUnpaid = ["awaiting_payment", "pending", "draft"].includes(order.status);
                            const prefix = isUnpaid ? "Carepoint-ProForma" : "Carepoint-Invoice";
                            link.href = url;
                            link.setAttribute('download', `${prefix}-${order.id.slice(0,8).toUpperCase()}.pdf`);
                            document.body.appendChild(link);
                            link.click();
                            link.remove();
                          } catch (err) {
                            console.error('Failed to download PDF:', err);
                          }
                        }}
                        className="text-[#00a99d] hover:text-[#008f85]"
                        title={["awaiting_payment", "pending", "draft"].includes(order.status) ? "Download Pro Forma" : "Download Invoice"}
                        data-testid={`admin-invoice-${order.id}`}
                      >
                        <FileText className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => emailOrderDocument(order.id)}
                        className={`hover:text-[#00a99d] ${order.last_email_sent_at ? "text-emerald-600" : "text-gray-400"}`}
                        title={order.last_email_sent_at
                          ? `Already emailed ${new Date(order.last_email_sent_at).toLocaleString()} — click to resend`
                          : "Email order confirmation / pro forma to customer"}
                        data-testid={`admin-email-${order.id}`}
                      >
                        <Mail className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => exportSingleOrderCsv(order.id)}
                        className="text-gray-400 hover:text-[#00a99d]"
                        title="Download this order as CSV"
                        data-testid={`admin-export-${order.id}`}
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => deleteOrder(order.id)}
                        className="text-red-400 hover:text-red-600"
                        title="Delete Order"
                        data-testid={`admin-delete-order-${order.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Order Tab */}
      {activeTab === "create-order" && (
        <AdminCreateOrder
          onToast={setToast}
          onCreated={(newOrder) => {
            setOrders((prev) => [newOrder, ...prev]);
            setActiveTab("orders");
          }}
        />
      )}

      {/* Credit Accounts Tab */}
      {activeTab === "credit" && (
        <CreditAccounts onToast={setToast} />
      )}

      {/* Customers Tab */}
      {activeTab === "customers" && (
        <div className="card overflow-x-auto" data-testid="admin-customers">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Joined</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {customers.map((customer) => (
                <tr key={customer.id}>
                  <td className="font-medium">{customer.name}</td>
                  <td>{customer.email}</td>
                  <td><span className={`badge ${customer.role === "admin" ? "bg-purple-100 text-purple-700" : "bg-blue-100 text-blue-700"}`}>{customer.role}</span></td>
                  <td>{new Date(customer.created_at).toLocaleDateString()}</td>
                  <td>
                    {customer.role !== "admin" ? (
                      <button
                        onClick={() => deleteCustomer(customer.id)}
                        className="text-red-400 hover:text-red-600"
                        title="Delete Customer"
                        data-testid={`delete-customer-${customer.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    ) : (
                      <span className="text-xs text-gray-400">Protected</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Products Tab */}
      {activeTab === "products" && (
        <div data-testid="admin-products">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Product Management</h3>
            <button onClick={openAddProduct} className="btn-primary flex items-center gap-2" data-testid="add-product-btn">
              <Plus className="w-4 h-4" /> Add Product
            </button>
          </div>

          {/* Product Form Modal */}
          {showProductForm && (
            <div className="card p-6 mb-6 border-2 border-[#00a99d]" data-testid="product-form">
              <h4 className="font-semibold mb-4">{editProduct ? "Edit Product" : "Add New Product"}</h4>
              <form onSubmit={handleSaveProduct} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Product Name *</label>
                  <input required className="form-input" value={productForm.name} onChange={e => setProductForm({...productForm, name: e.target.value})} data-testid="product-name-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">SKU *</label>
                  <input required className="form-input" value={productForm.sku} onChange={e => setProductForm({...productForm, sku: e.target.value})} data-testid="product-sku-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Price (&pound;) *</label>
                  <input required type="number" step="0.01" className="form-input" value={productForm.price} onChange={e => setProductForm({...productForm, price: e.target.value})} data-testid="product-price-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Original Price (&pound;)</label>
                  <input type="number" step="0.01" className="form-input" value={productForm.original_price} onChange={e => setProductForm({...productForm, original_price: e.target.value})} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Category *</label>
                  <input required className="form-input" value={productForm.category} onChange={e => setProductForm({...productForm, category: e.target.value})} list="categories-list" data-testid="product-category-input" />
                  <datalist id="categories-list">
                    <option value="Ink Cartridges" /><option value="Toner Cartridges" />
                    <option value="Printer Drums" /><option value="Care Supplies" />
                    <option value="Dental Supplies" /><option value="Vet Supplies" />
                    <option value="Stationery" /><option value="Paper & Hygiene" />
                    <option value="First Aid" /><option value="Gloves & PPE" />
                    <option value="Infection Control" /><option value="Medical Furniture" />
                    <option value="Pharmaceuticals" /><option value="Diagnostics" />
                    <option value="Emergency Care" /><option value="Syringes & Needles" />
                    <option value="Medical Supplies" />
                  </datalist>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Brand</label>
                  <input className="form-input" value={productForm.brand} onChange={e => setProductForm({...productForm, brand: e.target.value})} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Image URL</label>
                  <input className="form-input" value={productForm.image_url} onChange={e => setProductForm({...productForm, image_url: e.target.value})} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Stock</label>
                  <input type="number" className="form-input" value={productForm.stock} onChange={e => setProductForm({...productForm, stock: e.target.value})} />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-1">Description</label>
                  <textarea className="form-input h-20" value={productForm.description} onChange={e => setProductForm({...productForm, description: e.target.value})} />
                </div>
                <div className="md:col-span-2 flex gap-3">
                  <button type="submit" disabled={savingProduct} className="btn-primary" data-testid="save-product-btn">
                    {savingProduct ? "Saving..." : (editProduct ? "Update Product" : "Add Product")}
                  </button>
                  <button type="button" onClick={() => setShowProductForm(false)} className="px-4 py-2 border rounded hover:bg-gray-50">Cancel</button>
                </div>
              </form>
            </div>
          )}

          <div className="card overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Image</th>
                  <th>Name</th>
                  <th>SKU</th>
                  <th>Price</th>
                  <th>Category</th>
                  <th>Stock</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {products.map((product) => (
                  <tr key={product.id}>
                    <td><img src={product.image_url || "https://via.placeholder.com/40"} alt="" className="w-10 h-10 object-contain" /></td>
                    <td className="max-w-xs truncate">{product.name}</td>
                    <td className="font-mono text-sm">{product.sku}</td>
                    <td>&pound;{product.price.toFixed(2)}</td>
                    <td>{product.category}</td>
                    <td>{product.stock}</td>
                    <td className="flex gap-2">
                      <button onClick={() => openEditProduct(product)} className="text-blue-500 hover:text-blue-700" data-testid={`edit-product-${product.id}`}>
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button onClick={() => deleteProduct(product.id)} className="text-red-500 hover:text-red-700">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Scraper Tab */}
      {activeTab === "scraper" && (
        <div className="card p-6 max-w-xl" data-testid="admin-scraper">
          <h3 className="font-semibold mb-4">Product Scraper</h3>
          <p className="text-sm text-gray-500 mb-4">
            Scrape products from Next Day Care Supplies or other supplier websites and add them to your store.
          </p>
          <form onSubmit={handleScrape} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">URL to Scrape</label>
              <input
                type="url"
                required
                className="form-input"
                value={scrapeUrl}
                onChange={(e) => setScrapeUrl(e.target.value)}
                placeholder="https://nextdaycaresupplies.co.uk/collections/medical-supplies"
                data-testid="scrape-url-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Category</label>
              <select
                className="form-input"
                value={scrapeCategory}
                onChange={(e) => setScrapeCategory(e.target.value)}
                data-testid="scrape-category-select"
              >
                <option value="Medical Supplies">Medical Supplies</option>
                <option value="Care Equipment">Care Equipment</option>
                <option value="PPE & Safety">PPE &amp; Safety</option>
                <option value="Cleaning Supplies">Cleaning Supplies</option>
                <option value="Ink Cartridges">Ink Cartridges</option>
                <option value="Toner Cartridges">Toner Cartridges</option>
                <option value="Printer Drums">Printer Drums</option>
                <option value="Paper & Hygiene">Paper &amp; Hygiene</option>
                <option value="First Aid">First Aid</option>
                <option value="Gloves & PPE">Gloves &amp; PPE</option>
                <option value="Infection Control">Infection Control</option>
                <option value="Medical Furniture">Medical Furniture</option>
                <option value="Pharmaceuticals">Pharmaceuticals</option>
                <option value="Diagnostics">Diagnostics</option>
                <option value="Emergency Care">Emergency Care</option>
                <option value="Syringes & Needles">Syringes &amp; Needles</option>
              </select>
            </div>
            <button type="submit" disabled={scraping} className="btn-primary" data-testid="scrape-btn">
              {scraping ? "Scraping..." : "Start Scraping"}
            </button>
          </form>
        </div>
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}
